import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import {
  Fragment,
  cloneElement,
  isValidElement,
  useId,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type ReactElement,
  type ReactNode
} from 'react';

import { StatusChip } from '@/design';

import type {
  FieldTypeCode,
  IFinding,
  IGridColumn,
  IRenderState,
  ISnapshotUsage
} from '../api/contracts';
import { groupedAmount, malformedHint, type UiValue } from './answer-values';
import type { IPrefillView } from './prefill-view';
import { LinkedText } from './linked-text';
import { QuestionLabel } from './question-label';
import { SchoolList } from './school-list';
import {
  DAYS,
  MAX_TEXT_VALUE_CHARS,
  MONTHS,
  applyMask,
  controlShape,
  isMonthDay,
  lengthNotice,
  monthDayParts,
  resolveColumnConstraint,
  yearChoices
} from './field-constraints';
import {
  answerInstanceKey,
  domAddress,
  effectiveLabel,
  findingText,
  groupedOptions,
  mergeCellFindings,
  visibleOptions
} from './snapshot-view';
import { tableLayout } from './table-layout';
import { WarningLine } from './warning-line';

interface IResponseFieldProps {
  usage: ISnapshotUsage;
  /** The repeat row this field belongs to, or null for a scalar. Only used to address the
   *  field in the DOM — every value it drives arrives already resolved. */
  instanceId: Nullable<string>;
  render: IRenderState;
  value: UiValue;
  findings: IFinding[];
  required: boolean;
  /**
   * The typed text cannot be carried by this field type's payload, so nothing was sent
   * and nothing was cleared (`IUseResponse.malformed`). Not a finding and never rendered
   * as one: the engine has not seen this value and has no opinion on it. What it means is
   * "this is still on your screen and not yet on the server, and here is why".
   */
  malformed?: boolean;
  /** The response is no longer writable, or the usage is computed: nothing here is offered. */
  readOnly: boolean;
  /**
   * The prefill policy locks this answer against the respondent (`prefillLock` answered
   * `'all'`): the control is drawn read-only, and a proposal offers "Confirm" alone, since
   * survey-api refuses its reject `locked_usage` and any write `prefill_locked` — or nothing, where
   * a confirm is all there is (`confirmOnly`).
   */
  prefillLocked?: boolean;
  /**
   * The proposal is one the respondent can only confirm (`isConfirmOnly`): no strip and no
   * "Confirm" — its source alone, `data-prefill="locked"`.
   */
  confirmOnly?: boolean;
  /**
   * What prefill has to say about this field: the confirm strip while a proposal is
   * `proposed`, and what the value was carried over from once it is not. Null for the
   * ordinary case — an answer nobody proposed.
   */
  prefill?: Nullable<IPrefillView>;
  /**
   * The respondent has typed here since the last successful write, so the confirm strip is
   * withheld. `runAction` flushes the queue before any action, so a `Confirm` pressed over
   * an unsaved edit would send that edit first — moving the proposal to `overridden` — and
   * then confirm nothing, which survey-api refuses as `not_proposed`.
   */
  hasUnsavedEdit?: boolean;
  /** A proposal rejected in this session emptied this field — see `IUseResponse.clearedPrefill`. */
  cleared?: boolean;
  onChange: (value: UiValue) => void;
  /**
   * The value is finished — the respondent has left the control. Only typed
   * controls raise it; a choice is finished the moment it is made, so those save
   * from `onChange` and nothing here waits for a blur that may never come.
   */
  onCommit?: () => void;
  onConfirm?: (action: 'confirm' | 'reject') => void;
  /** Cells rows for grid / multi-year tables: instanceKey → cells value. */
  cellRows?: { key: string; label: string }[];
  cellValues?: Map<string, Record<string, string>>;
  /**
   * Findings addressed to a single row, keyed by its instance key. A cells answer
   * is one row per key, so the engine flags the row rather than the table, and
   * `findings` above only carries what is addressed at the table itself.
   */
  cellFindings?: Map<string, IFinding[]>;
  /** Row keys whose cells hold unparseable text — the per-row twin of `malformed`. */
  malformedRows?: ReadonlySet<string>;
  /**
   * A table row's cells its `locked_if_filled` proposal holds (`lockedCells`), by row key —
   * drawn read-only, the row's other cells enterable — with the source each was proposed from.
   */
  lockedRowCells?: Map<string, ILockedCells>;
  /** What prefill says about each table row, by row key — see `IRowPrefill`. */
  rowPrefill?: Map<string, IRowPrefill>;
  /** A row's "Confirm", at the row's own address. */
  onRowConfirm?: (rowKey: string) => void;
  onCellChange?: (rowKey: string, cells: Record<string, string>) => void;
  /**
   * Words after the label, for a question the wire names generically — a member's U4 follow-up
   * says whom it asks about ("— Jane Doe", ML §8: the label naming the member is this app's).
   */
  labelSuffix?: string;
  /** What the runtime says under the control — a CRD lookup's note (`LookupAside`). */
  aside?: ReactNode;
  /**
   * The follow-ups an option opened, drawn at that option (the plan's D15), by option code —
   * a radio's, a checkbox list's or a multi-select's alone (`underOption`).
   */
  optionFollowUps?: IOptionFollowUps;
  /**
   * A percent group's member (the survey review's R23, R28): its words and a narrow box on one
   * line, what is said about it under both.
   */
  compact?: boolean;
  /**
   * The one box an option opened, drawn on the option's line (Q9 a): a narrow box, its question's
   * words its accessible name alone — the option's words beside it say what it is for — and its
   * unit and required mark after it.
   */
  beside?: boolean;
}

/**
 * What a list's options opened (the plan's D15, Q9 a): the follow-ups by option code, and the
 * options whose follow-up is one box, drawn on the option's line from `sm` rather than under it.
 */
export interface IOptionFollowUps {
  byOption: ReadonlyMap<string, ReactNode>;
  beside: ReadonlySet<string>;
}

/** A table row's cells a `locked_if_filled` proposal holds, and how its source is named. */
export interface ILockedCells {
  columns: ReadonlySet<string>;
  /** "From our records" — the proposal's source, said to a screen reader on each locked cell. */
  sourceLabel: string;
}

/**
 * What prefill says about one table row (SR-416, B5-6): a cells answer is one proposal per row, so
 * its strip sits on the row's header rather than under the table.
 */
export interface IRowPrefill {
  view: IPrefillView;
  /** What the row's proposed cells held, for an `overridden` row (`proposedCellsText`). */
  proposedText: Nullable<string>;
  /** The row holds an unsaved edit, so its "Confirm" is withheld — `hasUnsavedEdit`'s rule. */
  hasUnsavedEdit: boolean;
}

const NUMERIC_INPUT_MODE: Partial<Record<FieldTypeCode, 'numeric' | 'decimal'>> = {
  int: 'numeric',
  year: 'numeric',
  number: 'decimal',
  currency: 'decimal',
  percent: 'decimal'
};

/** A checkbox list longer than this is filtered and laid in two columns (R12). */
const LONG_LIST = 20;

const HTML_INPUT_TYPE: Partial<Record<FieldTypeCode, string>> = {
  email: 'email',
  phone: 'tel',
  url: 'url',
  date: 'date'
};

/**
 * One question, rendered from its usage in the definition snapshot.
 *
 * This is the only place a `fieldType` becomes a control — which is what lets a
 * new survey ship as a seeded definition rather than as new components. What it
 * deliberately does **not** do is decide anything: visibility, requiredness,
 * which options are offered and every validation message are the engine's
 * outputs, passed in.
 */
export function ResponseField({
  usage,
  instanceId,
  render,
  value,
  findings,
  required,
  malformed,
  readOnly,
  prefillLocked = false,
  confirmOnly = false,
  prefill,
  hasUnsavedEdit,
  cleared,
  onChange,
  onCommit,
  onConfirm,
  cellRows,
  cellValues,
  cellFindings,
  malformedRows,
  lockedRowCells,
  rowPrefill,
  onRowConfirm,
  onCellChange,
  labelSuffix,
  aside,
  optionFollowUps,
  compact = false,
  beside = false
}: IResponseFieldProps) {
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  // The words a long checkbox list is narrowed by — the respondent's, on this screen alone.
  const [filter, setFilter] = useState('');
  const ownLabel = effectiveLabel(usage, render);
  const label = labelSuffix ? `${ownLabel} ${labelSuffix}` : ownLabel;
  const hardFinding = findings.find((finding) => finding.severity === 'hard');
  // The unsaveable-entry hint takes the error slot ahead of a finding, and the ordering is
  // the honest one rather than a preference: a malformed entry means the server never
  // received this value, so any finding sitting here judges the *previous* one and would
  // be answering a question the respondent is no longer asking.
  const unsaveable = malformed ? malformedHint(usage.fieldType) : null;
  const softFindings = findings.filter((finding) => finding.severity === 'soft');
  const options = visibleOptions(usage, render);

  const text = typeof value === 'string' ? value : '';
  const codes = Array.isArray(value) ? value : [];
  // Same string `data-address` renders below, computed once and reused for a radio's
  // `name`: without it, every instance of a `repeat_member` radio (or grid/repeat-row
  // radio generally) shares the bare usage code, and the browser's native radio-group
  // exclusivity — which groups by `name` alone, with no notion of a React subtree —
  // then treats every row's radio as one group, so checking a second row silently
  // reverts whichever row last re-rendered. Scoping `name` to the address keeps each
  // instance's group native-exclusive with only its own options.
  const address = domAddress(usage.code, instanceId, answerInstanceKey(usage));

  // The declared shape of this field, resolved server-side (SR-122) and read, never
  // re-derived. It says how to *ask* — the keyboard, the positional spelling, the
  // autofill hint — and decides nothing: whether a value is acceptable comes back as an
  // `engine.format` finding like every other judgement the engine makes.
  const constraint = usage.constraint;
  const shape = controlShape(constraint);
  const counter = lengthNotice(text, constraint);
  // Resolves this field's own usage for `findingText` — every finding rendered here is
  // addressed to it, so there is no lookup to do and nothing else to reach.
  const usageOf = () => usage;

  /**
   * Typed input, re-spelled in the constraint's mask when there is one.
   *
   * Only while the caret sits at the end. A mask on a controlled input rewrites the
   * whole string, and a rewrite the respondent did not ask for moves the caret to the
   * end of it — harmless while appending, destructive while correcting the third digit
   * of a phone number already typed. Editing mid-value therefore passes through raw, and
   * the server judges the raw value exactly as it judges a pasted one.
   *
   * `selectionStart` is **null**, not a number, on the input types it does not apply to —
   * `email` and `date` here, per the HTML spec's getter, which returns null rather than
   * throwing the way the setter does. Read as "at the end": those controls expose no
   * caret to track, so the alternative is a mask that silently never applies. No seeded
   * format puts one on either type today, which is exactly why that would have gone
   * unnoticed.
   */
  const onTyped = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const caret = event.target.selectionStart;
    const atEnd = caret === null || caret === raw.length;
    onChange(constraint?.mask && atEnd ? applyMask(constraint.mask, raw) : raw);
  };

  // An option's block: its own control, then what it opened — on the option's line from `sm` when
  // that is one box, under it otherwise. Always the same block, so ticking an option does not
  // remount the box that holds the focus.
  const withFollowUps = (code: string, choice: ReactNode) => {
    const followUps = optionFollowUps?.byOption.get(code);
    const oneBox = optionFollowUps?.beside.has(code) ?? false;
    return (
      <div
        key={code}
        data-option={code}
        className={cn('grid gap-3', oneBox && 'gap-1 sm:flex sm:items-center sm:gap-x-3')}
      >
        {choice}
        {followUps &&
          (oneBox ? (
            <div className="ml-4 sm:ml-0">{followUps}</div>
          ) : (
            <div className="ml-6 grid gap-4 border-l-2 border-[var(--color-slight-blue)] pl-4">
              {followUps}
            </div>
          ))}
      </div>
    );
  };

  const invalid = !!hardFinding || !!unsaveable;
  // What the control may take: nothing on a read-only response, and nothing over a lock.
  const disabled = readOnly || prefillLocked;
  // A decision is offered on a writable response, a lock's included — its "Confirm" is taken.
  const decides = prefill?.state === 'proposed' && !readOnly && !hasUnsavedEdit && !confirmOnly;
  // A proposal only a confirm settles: its source alone, nothing to decide.
  const settled = confirmOnly && prefill?.state === 'proposed';

  const control = () => {
    switch (usage.fieldType) {
      case 'display_note':
        return null;

      case 'text_area':
        return (
          <Textarea
            id={fieldId}
            rows={4}
            // The kit sets an invalid ring colour but no width, so on its own
            // only the border changes. Same width as the focus ring.
            className="aria-invalid:ring-[3px]"
            value={text}
            disabled={disabled}
            maxLength={shape.maxLength}
            aria-invalid={invalid}
            onChange={(event) => onChange(event.target.value)}
            onBlur={onCommit}
          />
        );

      case 'dropdown':
        return (
          <Select
            value={codes[0] ?? ''}
            disabled={disabled}
            onValueChange={(next) => onChange([next])}
          >
            <SelectTrigger
              id={fieldId}
              // The kit styles no invalid state for a trigger, so it is spelled
              // out here to match what `Input` does. A long option wraps in the trigger, which
              // grows to hold it (the survey review's FA8, FSP4, T5), rather than run out of it:
              // `h-auto!` over the site's fixed control height (`global.css`), which it keeps as
              // the least.
              className="h-auto! min-h-11 w-full text-left aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 [&>span]:min-w-0"
              aria-invalid={invalid}
            >
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            {/* The list is no wider than the screen, so a long option wraps in it too. */}
            <SelectContent className="max-w-[min(40rem,calc(100vw-2rem))]">
              {options.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'radio':
        return (
          <div role="radiogroup" aria-invalid={invalid} className="grid gap-2 pt-1">
            {options.map((option) =>
              withFollowUps(
                option.code,
                <label
                  className={cn(
                    'flex items-start gap-2 text-sm',
                    disabled && 'cursor-not-allowed opacity-60'
                  )}
                >
                  <input
                    type="radio"
                    name={address}
                    value={option.code}
                    checked={codes[0] === option.code}
                    disabled={disabled}
                    onChange={() => onChange([option.code])}
                    className="mt-0.5 size-4 accent-[var(--color-sapphire)]"
                  />
                  {option.label}
                </label>
              )
            )}
          </div>
        );

      case 'multi_select':
      case 'checkbox':
      case 'attestation': {
        const box = (option: (typeof options)[number]) =>
          withFollowUps(
            option.code,
            <label
              className={cn(
                'flex items-start gap-2 text-sm',
                disabled && 'cursor-not-allowed opacity-60'
              )}
            >
              <Checkbox
                checked={codes.includes(option.code)}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  // An exclusive option (a "None of the above") clears the
                  // rest: the write endpoint refuses the combination outright,
                  // so offering it would only earn a rejected batch.
                  if (checked && option.isExclusive) {
                    onChange([option.code]);
                    return;
                  }
                  const withoutExclusive = codes.filter(
                    (code) => !options.find((o) => o.code === code)?.isExclusive
                  );
                  onChange(
                    checked
                      ? [...withoutExclusive, option.code]
                      : withoutExclusive.filter((code) => code !== option.code)
                  );
                }}
              />
              {option.label}
            </label>
          );
        // A long list (the survey review's R12) is filtered by its words and laid in two columns
        // from `md`. The filter narrows only what the engine offers, and never hides a ticked box.
        const long = options.length > LONG_LIST;
        const query = filter.trim().toLowerCase();
        const kept = (option: (typeof options)[number]) =>
          !query || codes.includes(option.code) || option.label.toLowerCase().includes(query);
        const groups = groupedOptions(usage, render)
          .map((group) => ({ ...group, options: group.options.filter(kept) }))
          .filter((group) => group.options.length > 0);
        const columns = cn('grid gap-2 pt-1', long && 'md:grid-cols-2 md:gap-x-6');
        return (
          // `role="group"` so the invalid state has something to sit on: without
          // it there is no element a screen reader reads the flag from, the way
          // the radio list gets it from its radiogroup.
          <div role="group" aria-invalid={invalid} className="grid gap-2 pt-1">
            {long && (
              <Input
                type="search"
                aria-label={`Filter the options of: ${label}`}
                placeholder="Type to filter the options"
                className="max-w-sm"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              />
            )}
            {groups.some((group) => group.heading)
              ? // Under the headings survey-api keeps as hidden options (`groupedOptions`): each a
                // group named by its heading, every box's own name unchanged.
                // Other's group is headed by nothing, and keyed by its place (`groupedOptions`).
                groups.map((group, index) => (
                  <div
                    key={group.heading ?? `apart-${index}`}
                    role="group"
                    aria-label={group.heading ?? undefined}
                    data-option-group={group.heading ?? ''}
                    className="grid gap-2 pt-1"
                  >
                    {group.heading && (
                      <p
                        aria-hidden
                        className="text-xs font-medium tracking-wide text-muted-foreground uppercase"
                      >
                        {group.heading}
                      </p>
                    )}
                    <div className={columns}>{group.options.map(box)}</div>
                  </div>
                ))
              : groups.length > 0 && (
                  <div className={columns}>{groups[0].options.map(box)}</div>
                )}
            {groups.length === 0 && (
              <p className="text-sm text-muted-foreground" role="status">
                No option matches “{filter.trim()}”.
              </p>
            )}
          </div>
        );
      }

      case 'grid':
      case 'multi_year_table':
        return (
          <CellsTable
            usage={usage}
            instanceId={instanceId}
            render={render}
            rows={cellRows ?? []}
            values={cellValues ?? new Map()}
            findings={cellFindings ?? new Map()}
            malformedRows={malformedRows ?? new Set()}
            readOnly={disabled}
            decides={!readOnly}
            lockedCells={lockedRowCells ?? new Map()}
            rowPrefill={rowPrefill ?? new Map()}
            onRowConfirm={(rowKey) => onRowConfirm?.(rowKey)}
            onChange={(rowKey, cells) => onCellChange?.(rowKey, cells)}
            onCommit={onCommit}
          />
        );

      default: {
        // A year between two bounds and a month-day are picked, not typed (D13); a pick is a
        // finished value, so it is written at once.
        const years = yearChoices(usage.fieldType, constraint);
        if (years) {
          // A stored year the bounds leave out is still drawn, beside the engine's finding on it.
          const offered = text && !years.includes(text) ? [text, ...years] : years;
          return (
            <Select
              value={text}
              disabled={disabled}
              onValueChange={(next) => {
                onChange(next);
                onCommit?.();
              }}
            >
              <SelectTrigger
                id={fieldId}
                className="w-40 aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20"
                aria-invalid={invalid}
              >
                <SelectValue placeholder="Select a year" />
              </SelectTrigger>
              <SelectContent>
                {offered.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        if (isMonthDay(constraint)) {
          return (
            <MonthDayPicker
              id={fieldId}
              labelId={labelId}
              value={text}
              disabled={disabled}
              invalid={invalid}
              onPick={(next) => {
                onChange(next);
                onCommit?.();
              }}
            />
          );
        }
        // A school is a box per school suggesting US colleges (FA7, R13, T9, Q9 d); the answer
        // stays the text typed, the schools `; `-joined.
        if (usage.params?.suggest === 'colleges') {
          return (
            <SchoolList
              id={fieldId}
              label={label}
              value={text}
              disabled={disabled}
              maxLength={shape.maxLength}
              invalid={invalid}
              onChange={onChange}
              onCommit={onCommit}
            />
          );
        }
        const Box = textBoxFor(usage.fieldType);
        return (
          <Box
            id={fieldId}
            type={HTML_INPUT_TYPE[usage.fieldType] ?? 'text'}
            // The field type's keyboard, unless the constraint refines it: `decimals: 0`
            // on a `number`/`currency`/`percent` usage means a whole number, and a
            // decimal keypad on it offers a separator the value cannot carry.
            inputMode={shape.inputMode ?? NUMERIC_INPUT_MODE[usage.fieldType]}
            className={cn(
              'aria-invalid:ring-[3px]',
              (compact || beside) && 'h-9!',
              beside && (usage.fieldType === 'text_short' ? 'w-56' : 'w-32')
            )}
            value={text}
            disabled={disabled}
            placeholder={shape.placeholder}
            autoComplete={shape.autoComplete}
            // A date's latest day, resolved server-side (SR-416): the picker offers nothing
            // after it. A day typed past it is still taken and judged — `engine.format`.
            max={usage.fieldType === 'date' ? (constraint?.maxDate ?? undefined) : undefined}
            maxLength={shape.maxLength}
            aria-invalid={invalid}
            onChange={onTyped}
            onBlur={onCommit}
          />
        );
      }
    }
  };

  if (usage.fieldType === 'display_note') {
    return (
      <div className="rounded-md bg-[var(--color-slight-blue)] px-4 py-3 text-sm text-[var(--color-royal-blue)]">
        {label}
      </div>
    );
  }

  // The control takes the label's id, as the kit's form field gave it.
  const drawn = control();
  const cloned = isValidElement(drawn)
    ? cloneElement(drawn as ReactElement<{ id?: string }>, { id: fieldId })
    : drawn;
  // A box beside its option says its unit and its required mark after it, its words — where both
  // sit on every other question — being a screen reader's (the survey review's G17).
  const labelled = beside ? (
    <div className="flex items-center gap-1.5">
      {cloned}
      {usage.fieldType === 'percent' && (
        <span aria-hidden className="text-sm text-muted-foreground">
          %
        </span>
      )}
      {required && (
        <span aria-hidden className="text-sm text-destructive">
          *
        </span>
      )}
    </div>
  ) : (
    cloned
  );

  return (
    // The rule down the left is always present and normally transparent, so a
    // finding appearing cannot shift the section. It carries the highlight for
    // every control alike: the kit colours an invalid `Input`, `Textarea` and
    // `Checkbox` border, but a `SelectTrigger`, a radio list and a grid have no
    // invalid styling of their own, and a question the respondent has to find
    // must not be marked more faintly for being a dropdown.
    //
    // Only hard findings get it. A soft one does not block anything, and marking
    // it the same way would spend the strongest signal on the weaker problem.
    //
    // `answerInstanceKey`, not a literal `''`: a scalar `per_option_sub` — an "Other,
    // please specify" box and its 23 siblings — is answered and found at its own trigger
    // option code, not blank (`snapshot-view.ts`), and `findings-panel.tsx` stamps that
    // usage's `engine.required` finding at the same triple. Leaving this blank for those
    // 24 usages would desync the field's address from its own finding's — exactly the
    // divergence the runtime design §3 warns never to let happen.
    <div
      data-address={address}
      className={cn(
        // The padding is part of the reserved space, not of the highlight: laid
        // out always so the tint has room on every side of the question it marks,
        // and so switching it on is a colour change and nothing else. Symmetric
        // on purpose — an invalid control draws a 3px ring outside its own border,
        // so with no right padding the field visibly outruns the tint marking it.
        'min-w-0 rounded-r-md border-l-2 border-transparent transition-colors',
        compact || beside ? 'px-2 py-1' : 'px-3 py-2',
        invalid && 'border-destructive bg-destructive/5'
      )}
    >
      <div
        className={cn(
          'grid gap-2',
          compact && 'max-w-sm grid-cols-[minmax(0,1fr)_4rem] items-center gap-x-2',
          beside && 'max-w-xs gap-1'
        )}
      >
        {/* A compact box is the kit's 36 px, not the portal's 44, and its words the list's size. */}
        <QuestionLabel
          id={labelId}
          htmlFor={fieldId}
          text={label}
          required={required}
          className={cn(compact && 'text-sm! break-words hyphens-auto', beside && 'sr-only')}
        />
        {labelled}
        {usage.helperText && (
          <p className={cn('text-sm text-muted-foreground', compact && 'col-span-2')}>
            <LinkedText text={usage.helperText} />
          </p>
        )}
        {/* `'inline'`: the message renders directly under this question's own label, so it must not
            repeat it. The resolver is still passed — `engine.format` reads the format's respondent
            copy off this usage's constraint through it. */}
        {(unsaveable || hardFinding) && (
          <p className={cn('text-sm text-destructive', compact && 'col-span-2')}>
            {unsaveable ?? findingText(hardFinding!, usageOf, 'inline')}
          </p>
        )}
      </div>

      {counter && (
        // Only near the ceiling — see `lengthNotice`. A narrative answer that is about
        // to be flagged for length is the one place a counter is worth its space.
        <p className="mt-1 text-right text-xs text-muted-foreground">{counter}</p>
      )}

      {aside}

      {softFindings.map((finding) => (
        <WarningLine key={`${finding.code}-${finding.ruleId ?? ''}`} className="mt-2">
          {findingText(finding, usageOf, 'inline')}
        </WarningLine>
      ))}

      {/* The three states a proposal can be in, as data as well as copy: a spec asserting
          on "Previously answered" would be asserting on English. `rejected` is not among
          them — survey-api deletes the answer, so there is nothing left to render and
          `cleared` below is what speaks for it, for this session only. */}
      {decides && (
        // The value is on file but not yet the respondent's own. Rejecting clears it —
        // that is the server's rule, not a choice made here — and is not offered over a lock,
        // which survey-api refuses to reject (`locked_usage`): "Confirm" is the one decision.
        <div className="mt-3 flex flex-wrap items-center gap-2" data-prefill="proposed">
          {/* A team's or firm's name carried from its roster is asked about (PS §5): "Yes" and
              "No" are "Confirm" and "Not correct" — the same two decisions. */}
          <StatusChip tone="info">
            {prefill.question ?? `${prefill.proposedLabel} — please confirm`}
          </StatusChip>
          <Button type="button" size="sm" variant="outline" onClick={() => onConfirm?.('confirm')}>
            {prefill.question ? 'Yes' : 'Confirm'}
          </Button>
          {!prefillLocked && (
            <Button type="button" size="sm" variant="ghost" onClick={() => onConfirm?.('reject')}>
              {prefill.question ? 'No' : 'Not correct'}
            </Button>
          )}
        </div>
      )}

      {prefill?.state === 'overridden' && prefill.priorText && (
        <p className="mt-2 text-xs text-muted-foreground" data-prefill="overridden">
          {prefill.sourceLabel}: {prefill.priorText}
        </p>
      )}

      {prefill?.state === 'confirmed' && (
        <p className="mt-2 text-xs text-muted-foreground" data-prefill="confirmed">
          Confirmed
        </p>
      )}

      {cleared && (
        <p className="mt-2 text-sm text-[var(--color-royal-blue)]" data-prefill="cleared">
          Cleared — please enter your own answer.
        </p>
      )}

      {settled && prefill && (
        // Settled by the source, nothing left to decide (the survey review's FA1, T1).
        <p className="mt-2" data-prefill="locked">
          <StatusChip>{prefill.proposedLabel}</StatusChip>
        </p>
      )}

      {prefillLocked && prefill && !decides && !settled && (
        // Only with a provenance row behind it, and only where the confirm strip is not
        // already naming the source. The copy used to be a flat "From your Form ADV record" on
        // any locked usage, which named a record that — with no provider integration
        // populating anything — did not exist; the source now says which record it actually is.
        <StatusChip className="mt-2">{prefill.sourceLabel}</StatusChip>
      )}
    </div>
  );
}

/** A table row as the table draws it: its instance key and its head. */
type ITableRow = { key: string; label: string };

interface ICellsTableProps {
  usage: ISnapshotUsage;
  instanceId: Nullable<string>;
  /** The engine's render outputs: a table's rows and columns a rule hid or made required. */
  render: IRenderState;
  rows: ITableRow[];
  values: Map<string, Record<string, string>>;
  findings: Map<string, IFinding[]>;
  malformedRows: ReadonlySet<string>;
  readOnly: boolean;
  /** The response takes decisions — it is writable — whatever a table-wide lock holds. */
  decides: boolean;
  /** Each row's cells its proposal locks — see `IResponseFieldProps.lockedRowCells`. */
  lockedCells: Map<string, ILockedCells>;
  rowPrefill: Map<string, IRowPrefill>;
  onRowConfirm: (rowKey: string) => void;
  onChange: (rowKey: string, cells: Record<string, string>) => void;
  onCommit?: () => void;
}

/**
 * Grids and multi-year tables are the same control: rows addressed by
 * `instance_key` — a row option code for a grid, `y-1`/`y-2` for a year table —
 * each holding one `cells` document keyed by column code.
 *
 * **What a cell is comes from the layout, survey-api's own expansion** (`tableLayout`, SR-416),
 * and it is drawn, never inferred from a refusal (the roadmap's D4): a heading row is one row
 * header across the table, holding nothing; an N/A cell is empty, "Not applicable" to a screen
 * reader; a computed cell is its stored value as text — the engine's, from the last state
 * document, moving when a write lands — **whatever its column's `when` says**, the gate being for
 * typing (FA-PS-007's `TOTAL` headcount sits in a column gated on a cell that row has as N/A). A
 * row with no writable cell offers nothing to type.
 *
 * **Rows and columns are as the rules leave them** (`render.tableVisibility` / `tableRequired`):
 * one a rule hid is not drawn at all — its answer is the engine's to exclude, and is kept — and a
 * row a rule made required carries the mark on its header. A table no rule touched is absent from
 * both maps and drawn as it always was.
 *
 * **A cell a `locked_if_filled` proposal holds is drawn as a computed one is** (SR-416, B5-1):
 * its value as text, its source said to a screen reader, `data-cell-kind="locked"` — survey-api
 * refuses a row write that changes or drops it (`prefill_locked`). The row's other cells stay
 * enterable, and a row write still carries the locked cell as stored (B5-3): the draft starts from
 * the stored row, and a row write replaces the row.
 *
 * **A row's proposal is said on its header** (B5-6): the source and, for a figures row, its day,
 * with a "Confirm" named for the row while it is `proposed` (never "Not correct" over a locked row —
 * `locked_usage`); the source once `confirmed`; the source and the proposed cells once
 * `overridden`. The row carries `data-prefill=<state>`.
 *
 * **An `in_row` column has no head** (SR-469's hint, the plan's D10): its one live cell — an
 * "Other, please specify" box — sits under its row's label, inside the row's header, and is drawn
 * only while its `when` opens it; the other rows draw nothing for it, and its row is set apart by a
 * rule above.
 *
 * **A year table with `years_across` is drawn transposed** (SR-469's hint, D10): a column per year,
 * headed by the year row's words and carrying its address, and a row per column. Storage and
 * addresses are a year row's as ever; a year's findings are said under the table.
 *
 * Each cell is `[data-cell=<column>][data-cell-kind]` — a `td`, or the `in_row` cell's box in the
 * row header — and a transposed cell also `[data-row=<year key>]`, so a spec finds a cell by its
 * column and row, not by a position a hidden column would move; each control is named
 * "<row>, <column>".
 */
function CellsTable({
  usage,
  instanceId,
  render,
  rows,
  values,
  findings,
  malformedRows,
  readOnly,
  decides,
  lockedCells,
  rowPrefill,
  onRowConfirm,
  onChange,
  onCommit
}: ICellsTableProps) {
  const layout = tableLayout(usage);
  const shownRows = render.tableVisibility?.[usage.code]?.rows;
  const shownColumns = render.tableVisibility?.[usage.code]?.columns;
  const requiredRows = render.tableRequired?.[usage.code]?.rows;
  const allColumns: IGridColumn[] =
    usage.params?.grid?.columns ?? usage.params?.multi_year?.columns ?? [];
  const shown = allColumns.filter((column) => shownColumns?.[column.code] !== false);
  const columns = shown.filter((column) => !column.in_row);
  const inRowColumns = shown.filter((column) => column.in_row);
  const drawn = rows.filter((row) => shownRows?.[row.key] !== false);
  // One `tbody` per heading row and the rows under it, so its `th scope="rowgroup"` heads those
  // rows alone — in one body, every heading would head every row after it.
  const groups: { key: string; rows: typeof drawn }[] = [];
  for (const row of drawn) {
    if (groups.length === 0 || layout.isHeading(row.key)) groups.push({ key: row.key, rows: [] });
    groups[groups.length - 1].rows.push(row);
  }

  // A cell its gate shuts leaves the row (D8): unticking a role empties the headcount it opened, in
  // the write and in the draft, so its typing — "not a number" included — goes with it.
  const setCell = (rowKey: string, column: string, cell: string) => {
    const next: Record<string, string> = { ...(values.get(rowKey) ?? {}), [column]: cell };
    for (const gated of allColumns) {
      const gate = gated.when;
      if (gate?.cell === column && String(next[column] ?? '') !== String(gate.eq ?? '')) {
        delete next[gated.code];
      }
    }
    onChange(rowKey, next);
  };

  /** What a row holds, and how its findings mark it. */
  const rowOf = (row: ITableRow) => {
    const rowFindings = findings.get(row.key) ?? [];
    const unsaveable = malformedRows.has(row.key);
    return {
      cells: values.get(row.key) ?? {},
      locked: lockedCells.get(row.key),
      prefill: rowPrefill.get(row.key),
      findings: rowFindings,
      unsaveable,
      // A finding naming a cell marks that cell (SR-122, SR-416); one about the whole row, or text
      // no payload can carry, marks every cell of it.
      blocked:
        unsaveable || rowFindings.some((finding) => finding.severity === 'hard' && !finding.cell),
      tinted: unsaveable || rowFindings.some((finding) => finding.severity === 'hard')
    };
  };
  type IRowState = ReturnType<typeof rowOf>;

  /** One cell's kind and what it draws; `gated` while its `when` shuts it. */
  const cellOf = (
    row: ITableRow,
    state: IRowState,
    column: IGridColumn
  ): { kind: 'na' | 'computed' | 'locked' | 'input'; gated?: boolean; body: ReactNode } => {
    const kind = layout.kind(row.key, column.code);
    if (kind === 'na') {
      return {
        kind,
        body: (
          <span aria-hidden className="text-muted-foreground/60">
            —
          </span>
        )
      };
    }
    if (kind === 'computed') {
      return {
        kind,
        body: (
          <>
            {computedText(state.cells[column.code])}
            <span className="sr-only"> Worked out from other answers</span>
          </>
        )
      };
    }
    if (state.locked?.columns.has(column.code)) {
      return {
        kind: 'locked',
        body: (
          <>
            {computedText(state.cells[column.code])}
            <span className="sr-only"> {state.locked.sourceLabel}</span>
          </>
        )
      };
    }
    // A `when` clause makes a cell conditional on a sibling cell in the same row — the engine
    // reads it the same way.
    const gate = column.when;
    if (gate && String(state.cells[gate.cell] ?? '') !== String(gate.eq ?? '')) {
      return {
        kind: 'input',
        gated: true,
        body: <span className="text-muted-foreground">—</span>
      };
    }
    const cellInvalid = state.findings.some(
      (finding) => finding.cell === column.code && finding.severity === 'hard'
    );
    return {
      kind: 'input',
      body: (
        <CellControl
          column={column}
          name={`${row.label}, ${column.label ?? column.code}`}
          value={state.cells[column.code] ?? ''}
          invalid={cellInvalid || state.blocked}
          readOnly={readOnly}
          onChange={(next) => setCell(row.key, column.code, next)}
          onCommit={onCommit}
        />
      )
    };
  };

  const cellClass = (kind: string) =>
    cn(
      'py-1.5 pr-4',
      kind === 'computed' && 'font-medium tabular-nums',
      kind === 'locked' && 'tabular-nums'
    );

  /** A row's words, and the mark of a row a rule made required. */
  const rowHead = (row: ITableRow) => (
    <>
      {row.label}
      {requiredRows?.[row.key] === true && (
        <>
          <span aria-hidden className="ml-0.5 text-destructive">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </>
      )}
    </>
  );

  const prefillNote = (row: ITableRow, state: IRowState) =>
    state.prefill && (
      <RowPrefillNote
        prefill={state.prefill}
        rowLabel={row.label}
        decides={decides}
        onConfirm={() => onRowConfirm(row.key)}
      />
    );

  /** A row's own findings, each "<row> — …"; its missing cells in one line (`mergeCellFindings`). */
  const rowLines = (row: ITableRow, state: IRowState) => (
    <>
      {state.unsaveable && (
        <p className="text-sm font-medium text-destructive">
          {row.label} — {malformedHint(usage.fieldType)}
        </p>
      )}
      {mergeCellFindings(state.findings).map((finding) => {
        const key =
          `${finding.code}-${finding.ruleId ?? ''}-${finding.severity}-${finding.cell ?? ''}`;
        const words = `${row.label} — ${findingText(finding, () => usage, 'inline')}`;
        return finding.severity === 'hard' ? (
          <p key={key} className="text-sm font-medium text-destructive">
            {words}
          </p>
        ) : (
          <WarningLine key={key}>{words}</WarningLine>
        );
      })}
    </>
  );

  if (usage.params?.multi_year?.years_across) {
    const years = drawn.map((row) => ({ row, state: rowOf(row) }));
    return (
      // Positioned, so it clips the `sr-only` text in its heads: otherwise that text is placed
      // against the page and widens it on a phone.
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[28rem] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th scope="col" className="py-2 pr-4 font-medium" />
              {years.map(({ row, state }) => (
                // A year's head carries the year row's address, as the row does when years run down.
                <th
                  key={row.key}
                  scope="col"
                  data-address={domAddress(usage.code, instanceId, row.key)}
                  data-prefill={state.prefill?.view.state}
                  className={cn(
                    'py-2 pr-4 align-bottom font-medium',
                    state.tinted && 'bg-destructive/5'
                  )}
                >
                  {rowHead(row)}
                  {prefillNote(row, state)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {columns.map((column) => (
              <tr key={column.code} className="border-b border-border last:border-0">
                <th scope="row" className="max-w-64 py-2 pr-4 text-left font-normal">
                  {column.label ?? column.code}
                </th>
                {years.map(({ row, state }) => {
                  const { kind, body } = cellOf(row, state, column);
                  return (
                    <td
                      key={row.key}
                      data-cell={column.code}
                      data-row={row.key}
                      data-cell-kind={kind}
                      aria-label={kind === 'na' ? 'Not applicable' : undefined}
                      className={cn(cellClass(kind), state.tinted && 'bg-destructive/5')}
                    >
                      {body}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {/* The years' findings under the table, in the order of their heads. */}
        {years.map(({ row, state }) => (
          <Fragment key={row.key}>{rowLines(row, state)}</Fragment>
        ))}
      </div>
    );
  }

  return (
    // Positioned for the reason the years-across table's scroller is.
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[28rem] text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th scope="col" className="py-2 pr-4 font-medium" />
            {columns.map((column) => (
              // The column's words as survey-api cased them, wrapping under a long label (D10).
              <th key={column.code} scope="col" className="py-2 pr-4 align-bottom font-medium">
                {column.label ?? column.code}
              </th>
            ))}
          </tr>
        </thead>
        {groups.map((group, index) => {
          // The table's last row draws no rule; a group's last row keeps it above the next heading.
          const lastGroup = index === groups.length - 1;
          return (
            <tbody key={group.key}>
              {group.rows.map((row) => {
                if (layout.isHeading(row.key)) {
                  return (
                    <tr
                      key={row.key}
                      data-heading-row={row.key}
                      className="border-b border-border"
                    >
                      <th
                        scope="rowgroup"
                        colSpan={columns.length + 1}
                        className="pt-4 pb-1.5 text-left font-medium text-[var(--color-royal-blue)]"
                      >
                        {row.label}
                      </th>
                    </tr>
                  );
                }

                const state = rowOf(row);
                // The row and its findings' line under it are drawn alike.
                const rowClass = cn(
                  'border-b border-border',
                  lastGroup && 'last:border-0',
                  state.tinted && 'bg-destructive/5'
                );
                // The `in_row` cells this row holds: the one row each such column is live in.
                const inRow = inRowColumns.filter(
                  (column) => layout.kind(row.key, column.code) !== 'na'
                );

                return (
                  <Fragment key={row.key}>
                    <tr
                      data-address={domAddress(usage.code, instanceId, row.key)}
                      data-prefill={state.prefill?.view.state}
                      // A row holding an `in_row` cell — an "Other" — is set apart by a rule above.
                      className={cn(rowClass, inRow.length > 0 && 'border-t-2')}
                    >
                      <th scope="row" className="max-w-64 py-2 pr-4 text-left font-normal">
                        {rowHead(row)}
                        {/* An `in_row` cell sits under the row's label, its column's words above
                            the box, and is drawn only while its `when` lets it be typed (D10). */}
                        {inRow.map((column) => {
                          const { kind, gated, body } = cellOf(row, state, column);
                          return (
                            <div
                              key={column.code}
                              data-cell={column.code}
                              data-cell-kind={kind}
                              className="mt-1.5 grid gap-1"
                            >
                              {!gated && (
                                <>
                                  <span aria-hidden className="text-xs text-muted-foreground">
                                    {column.label ?? column.code}
                                  </span>
                                  {body}
                                </>
                              )}
                            </div>
                          );
                        })}
                        {prefillNote(row, state)}
                      </th>
                      {columns.map((column) => {
                        const { kind, body } = cellOf(row, state, column);
                        return (
                          <td
                            key={column.code}
                            data-cell={column.code}
                            data-cell-kind={kind}
                            aria-label={kind === 'na' ? 'Not applicable' : undefined}
                            className={cellClass(kind)}
                          >
                            {body}
                          </td>
                        );
                      })}
                    </tr>

                    {/* A row's own findings, under the row they belong to. The engine
                        addresses a cells answer per instance key, so without this a
                        refused row was flagged in the stepper and nowhere else. */}
                    {(state.findings.length > 0 || state.unsaveable) && (
                      <tr className={rowClass}>
                        <td colSpan={columns.length + 1} className="pb-2">
                          {rowLines(row, state)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

/**
 * A table row's prefill, under its label: the source — and a figures row's day — then, while the
 * row is `proposed` on a writable response with no unsaved edit, its "Confirm".
 */
function RowPrefillNote({
  prefill,
  rowLabel,
  decides,
  onConfirm
}: {
  prefill: IRowPrefill;
  rowLabel: string;
  /** The response takes decisions: it is writable. A table a `locked` policy holds still does. */
  decides: boolean;
  onConfirm: () => void;
}) {
  const { view } = prefill;
  const source = view.asOfText ? `${view.proposedLabel}, as of ${view.asOfText}` : view.proposedLabel;

  return (
    <div className="mt-1 grid justify-items-start gap-1 text-xs font-normal text-muted-foreground">
      {view.state === 'proposed' && <span>{source}</span>}
      {view.state === 'confirmed' && <span>{source} · Confirmed</span>}
      {view.state === 'overridden' && (
        <span>
          {view.sourceLabel}
          {prefill.proposedText ? `: ${prefill.proposedText}` : ''}
        </span>
      )}
      {view.state === 'proposed' && decides && !prefill.hasUnsavedEdit && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7"
          aria-label={`Confirm ${rowLabel}`}
          onClick={onConfirm}
        >
          Confirm
        </Button>
      )}
    </div>
  );
}

/**
 * A read-only cell's stored value — a computed one, or one a proposal locks — as text: a number
 * grouped as the rest of the app groups it (`en-US`), "—" where it is blank. Display only — the
 * value is survey-api's.
 */
function computedText(value: string | undefined): string {
  if (value === undefined || value === '') return '—';
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString('en-US', { maximumFractionDigits: 6 })
    : value;
}

/**
 * A cell holds no `opts` payload even when it is picked rather than typed, so the
 * hook cannot tell the two apart by field type the way it does elsewhere. The
 * distinction is made here instead: a picked cell commits on change, because its
 * control keeps focus after the click and would otherwise wait for a blur that
 * only comes when the respondent leaves the table.
 */
function CellControl({
  column,
  name,
  value,
  invalid,
  readOnly,
  onChange,
  onCommit
}: {
  column: IGridColumn;
  /** "<row>, <column>" — the control's accessible name, since a cell has no label of its own. */
  name: string;
  value: string;
  /** The row this cell is in carries a hard finding. */
  invalid?: boolean;
  readOnly: boolean;
  onChange: (value: string) => void;
  onCommit?: () => void;
}) {
  if (column.input === 'readonly_text') {
    return <span className="text-muted-foreground">{value || '—'}</span>;
  }

  if (column.input === 'checkbox') {
    return (
      <Checkbox
        checked={value === 'true'}
        aria-label={name}
        aria-invalid={invalid}
        disabled={readOnly}
        onCheckedChange={(checked) => {
          onChange(checked ? 'true' : '');
          onCommit?.();
        }}
      />
    );
  }

  if (column.input === 'dropdown' || column.input === 'radio') {
    return (
      <Select
        value={value}
        disabled={readOnly}
        onValueChange={(next) => {
          onChange(next);
          onCommit?.();
        }}
      >
        <SelectTrigger
          className="w-full min-w-32 aria-invalid:border-destructive"
          aria-label={name}
          aria-invalid={invalid}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {(column.options ?? []).map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // A column's constraint is the one the client merges: the server ships it as authored
  // and leaves the column-input defaults here (field-constraints design §11). `decimals`
  // is what makes an `int` column integral — a cell is validated by no value schema at
  // all — so it, and not the input's name, decides the keyboard.
  const constraint = resolveColumnConstraint(column);
  // `text` is the only non-numeric input left by the branches above, and it was getting
  // a decimal keypad from the old `input === 'int' ? … : 'decimal'` test.
  const inputMode =
    column.input === 'text' ? undefined : constraint.decimals === 0 ? 'numeric' : 'decimal';

  if (column.input === 'text' && column.suggest === 'colleges') {
    return (
      <SchoolList
        className="min-w-40"
        label={name}
        maxLength={MAX_TEXT_VALUE_CHARS}
        value={value}
        invalid={invalid}
        disabled={readOnly}
        onChange={onChange}
        onCommit={onCommit}
      />
    );
  }

  const Box = column.input === 'currency' ? AmountInput : Input;
  return (
    <Box
      className="min-w-24"
      aria-label={name}
      inputMode={inputMode}
      // A text cell is held as a scalar text is (`controlShape`): its 255 (`max_length`) is
      // survey-api's finding, never a cut — `maxLength` here would drop a paste's tail in
      // silence. It is capped at the scalar's `MAX_TEXT_VALUE_CHARS` by the owner's ruling
      // (2026-10-18), not by a refusal: survey-api's 64 KiB guard reads a text value, not a cell.
      maxLength={column.input === 'text' ? MAX_TEXT_VALUE_CHARS : undefined}
      value={value}
      aria-invalid={invalid}
      disabled={readOnly}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onCommit}
    />
  );
}

/** The box a typed field is drawn in: an amount's and a website's have their own. */
function textBoxFor(fieldType: FieldTypeCode) {
  if (fieldType === 'currency') return AmountInput;
  if (fieldType === 'url') return WebsiteInput;
  return Input;
}

/**
 * An amount's box — a `currency` usage's or a `currency` column's (the survey review's G16, D11):
 * `$` drawn before it, the amount grouped (`groupedAmount`) while the box is not focused and the
 * text as typed while it is, so a respondent edits what they typed and reads it grouped. Display
 * only: the value handed up is the box's text, and `encodeAnswer` strips the grouping as it strips
 * a typed one. A count, a CRD, a year and a percent are not amounts and stay ungrouped.
 *
 * **The swap to the typed text happens in the focus event, on the element.** Left to the
 * re-render, it lands after a Tab's or a script's select-all, and a value written into an input
 * drops its selection, so what is typed next was appended to the amount instead of replacing it.
 * A selection over the whole amount, or none yet — Chromium's `select()` focuses first and sets
 * its range after the event — becomes a selection over the whole typed text; any other is carried
 * over digit for digit (`typedOffset`); a click places its caret after the event, on the typed
 * text. React then renders the value the element already holds and leaves it alone.
 */
function AmountInput({ value, className, onFocus, onBlur, ...props }: ComponentProps<typeof Input>) {
  const [focused, setFocused] = useState(false);
  const text = typeof value === 'string' ? value : '';
  return (
    <div className="relative">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground"
      >
        $
      </span>
      <Input
        {...props}
        className={cn('pl-6', className)}
        value={focused ? text : groupedAmount(text)}
        onFocus={(event) => {
          const input = event.currentTarget;
          const shown = input.value;
          if (shown !== text) {
            const start = input.selectionStart ?? 0;
            const end = input.selectionEnd ?? 0;
            input.value = text;
            if (start === 0 && (end === 0 || end === shown.length)) {
              input.setSelectionRange(0, text.length);
            } else {
              input.setSelectionRange(typedOffset(shown, text, start), typedOffset(shown, text, end));
            }
          }
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
      />
    </div>
  );
}

/** What an empty website box starts with once focused (the survey review's FA2, R3). */
const WEBSITE_START = 'www.';
/** An address's own start, which the box's start does not go in front of. */
const OWN_STARTS = ['http://', 'https://', WEBSITE_START];

/**
 * A website's box (`url`, the survey review's FA2 and R3; the plan's D17): focused empty, it holds
 * "www." with the caret after it, so the respondent types the rest; left with nothing after it, it
 * is empty again. The value is what the box holds — "www." is typed for the respondent, never added
 * to what they typed, and `https://…` typed over it stays as typed.
 *
 * The start is written on the element in the focus event, as `AmountInput` swaps its text: a
 * select-all after the focus (a Tab's, Playwright's `fill`) then covers it, so typing replaces it.
 * Nothing is sent for it: the box's draft becomes "www." only once more is typed. An address
 * pasted or typed after it whole — its own `https://` or "www." — keeps its own start, not two.
 */
function WebsiteInput({ value, onFocus, onChange, onBlur, ...props }: ComponentProps<typeof Input>) {
  const [started, setStarted] = useState(false);
  const text = typeof value === 'string' ? value : '';
  return (
    <Input
      {...props}
      value={started && text === '' ? WEBSITE_START : text}
      onFocus={(event) => {
        const input = event.currentTarget;
        if (input.value === '') {
          input.value = WEBSITE_START;
          input.setSelectionRange(WEBSITE_START.length, WEBSITE_START.length);
          setStarted(true);
        }
        onFocus?.(event);
      }}
      onChange={(event) => {
        const input = event.currentTarget;
        const after = input.value.slice(WEBSITE_START.length);
        if (
          started &&
          input.value.startsWith(WEBSITE_START) &&
          OWN_STARTS.some((start) => after.toLowerCase().startsWith(start))
        ) {
          input.value = after;
        }
        onChange?.(event);
      }}
      onBlur={(event) => {
        setStarted(false);
        // The start alone, typed back to, is still nothing typed: the box is emptied before the
        // value is committed.
        const input = event.currentTarget;
        if (input.value === WEBSITE_START && text !== '') {
          input.value = '';
          onChange?.({ target: input, currentTarget: input } as ChangeEvent<HTMLInputElement>);
        }
        onBlur?.(event);
      }}
    />
  );
}

/**
 * A `month_day` asked as a month list and a day list (the survey review's R16, D13), composing the
 * `MM/DD` text a typed box would have held. Nothing is written until both are picked: one part alone
 * is held here, so a half-made date never reaches survey-api as a format finding. Each list is named
 * by the question and its own word ("Month", "Day").
 */
function MonthDayPicker({
  id,
  labelId,
  value,
  disabled,
  invalid,
  onPick
}: {
  id: string;
  /** The question's label, which names both lists. */
  labelId: string;
  value: string;
  disabled: boolean;
  invalid: boolean;
  onPick: (value: string) => void;
}) {
  const stored = monthDayParts(value);
  const [held, setHeld] = useState<{ month?: string; day?: string }>({});
  const month = held.month ?? stored.month;
  const day = held.day ?? stored.day;

  const pick = (part: 'month' | 'day', code: string) => {
    const next = { month, day, [part]: code };
    if (next.month && next.day) {
      setHeld({});
      onPick(`${next.month}/${next.day}`);
    } else {
      setHeld(next);
    }
  };

  const triggerClass =
    'aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20';
  return (
    <div className="flex flex-wrap gap-2">
      <span id={`${id}-month`} className="sr-only">
        Month
      </span>
      <span id={`${id}-day`} className="sr-only">
        Day
      </span>
      <Select value={month} disabled={disabled} onValueChange={(code) => pick('month', code)}>
        <SelectTrigger
          id={id}
          className={cn('w-40', triggerClass)}
          aria-labelledby={`${labelId} ${id}-month`}
          aria-invalid={invalid}
        >
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent>
          {MONTHS.map((option) => (
            <SelectItem key={option.code} value={option.code}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={day} disabled={disabled} onValueChange={(code) => pick('day', code)}>
        <SelectTrigger
          className={cn('w-24', triggerClass)}
          aria-labelledby={`${labelId} ${id}-day`}
          aria-invalid={invalid}
        >
          <SelectValue placeholder="Day" />
        </SelectTrigger>
        <SelectContent>
          {DAYS.map((code) => (
            <SelectItem key={code} value={code}>
              {Number(code)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Where an offset into the grouped text falls in the typed one: after as many digits, points and
 *  signs — the grouping and a typed `$` or space count for nothing. */
function typedOffset(shown: string, typed: string, offset: number): number {
  if (offset >= shown.length) return typed.length;
  const significant = /[\d.-]/;
  let count = [...shown.slice(0, offset)].filter((char) => significant.test(char)).length;
  for (let index = 0; index < typed.length; index++) {
    if (count === 0) return index;
    if (significant.test(typed[index])) count--;
  }
  return typed.length;
}
